/**
 * The new-campaign dialog (Figma `96:2`, `Organização · Nova campanha (TSE)`, file
 * `LrdQopN1jNWKJFWxOkn0IY`), opened from `TenantHome`'s header action and from
 * `CampaignCards`'s empty state.
 *
 * `CampaignCreate` carries no candidate field (`tenant/home/useCampaigns.ts`'s
 * `useCreateCampaign`), so a chosen candidature is attached with a second request, the record's
 * own `PUT /campaigns/{id}` -- the campaign always exists candidate-less for the instant between
 * the two. A 422 focuses the named field, and a refused creation (409) renders `api`'s
 * `_no_slot_available` figures, the same two numbers `NoSlots`/`OverSlots` draw on the
 * organisation's home.
 *
 * There is no timezone field: `CampaignCreate.timezone` needs a value, but nothing reachable from
 * this form names the campaign's location to derive one from -- no tenant, campaign or
 * registration schema carries a location field -- so the browser's own resolved zone is sent.
 *
 * The name field proposes `${ballot_name} ${ballot_number}` when a TSE registration is picked,
 * and stops proposing the moment the reader types into it themselves -- a suggestion, not a
 * lock. Start and end autofill from the selected election's own date the same way, for as long as
 * they stay untouched -- `Election` carries one date, not a start/end period, so both bounds
 * mirror it.
 *
 * Once the campaign exists, a third step names its allies and adversaries (`./Alliances`), with the
 * TSE record's suggestions one click away. It is skippable: closing the dialog there, by Pular or
 * otherwise, opens the record, since the campaign is already created.
 */
import { useId, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { CircleAlert } from 'lucide-react';

import { FormDialog } from '../../../chrome/form';
import { fieldControlProps, fieldErrorOf } from '../../../chrome/form/fieldErrorOf';
import { ApiFailureError, type ApiFailure } from '../../../lib/http/errors';
import { Alert, AlertDescription } from '../../../ui/alert';
import { Button } from '../../../ui/button';
import { Field, FieldError, FieldLabel } from '../../../ui/field';
import { Input } from '../../../ui/input';
import { cn } from '../../../ui/utils';
import { useApiClient } from '../../session/client';
import { CapacityStats } from '../../tenant/capacity/CapacityStats';
import { RequestSlots } from '../../tenant/capacity/RequestSlots';
import type { Capacity } from '../../tenant/capacity/api/schemas';
import { cm } from '../../tenant/capacity/messages';
import { useCreateCampaign } from '../../tenant/home/useCampaigns';
import { TseCandidatePicker } from '../tse/TseCandidatePicker';
import type { CampaignCandidateLink, LinkedCandidate } from '../tse/api/schemas';
import { AlliancesEditor } from './Alliances';
import { useSaveAlliances } from './api/queries';
import { campaignDetailSchema, type Alliance, type CampaignDetail } from './api/schemas';
import { LocalityStrip } from './LocalityStrip';
import { nm } from './newCampaignMessages';
import { RECORD_PATH } from './paths';

const DEFAULT_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

/** Candidatura and Identificação share the first page; Aliados e adversários is the third step. */
function Steps(props: { current: 1 | 3 }): ReactNode {
  const steps = [nm('step.candidate'), nm('step.identification'), nm('step.alliances')];
  return (
    <ol className="flex flex-wrap items-center gap-x-3 gap-y-1" aria-label={nm('steps')}>
      {steps.map((label, index) => {
        const active = props.current === 3 ? index === 2 : index < 2;
        return (
          <li
            key={label}
            aria-current={active ? 'step' : undefined}
            className={cn(
              'flex items-center gap-1.5 text-caption',
              active ? 'text-foreground' : 'text-muted-foreground',
            )}
          >
            <span
              data-numeric
              className={cn(
                'flex size-5 items-center justify-center rounded-full border text-caption-sm',
                active && 'border-primary bg-primary text-primary-foreground',
              )}
            >
              {index + 1}
            </span>
            {label}
          </li>
        );
      })}
    </ol>
  );
}

export function NewCampaignDialog(props: {
  tenantId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): ReactNode {
  const [name, setName] = useState('');
  const [nameTouched, setNameTouched] = useState(false);
  const [activeFrom, setActiveFrom] = useState('');
  const [activeTo, setActiveTo] = useState('');
  const [activeToTouched, setActiveToTouched] = useState(false);
  const [candidate, setCandidate] = useState<LinkedCandidate>(null);
  // Set once `mutation` succeeds, so a resubmit after the link step alone fails resumes on the
  // campaign already created rather than posting a second one into a second slot.
  const [createdCampaignId, setCreatedCampaignId] = useState<string | null>(null);
  const [step, setStep] = useState<'details' | 'alliances'>('details');
  const [alliances, setAlliances] = useState<Alliance[]>([]);
  const navigate = useNavigate();
  const client = useApiClient();
  const mutation = useCreateCampaign(props.tenantId);
  const saveAlliances = useSaveAlliances(createdCampaignId ?? '');
  // `useCreateCampaign`'s own mutation can't take the just-created id as a hook argument, so
  // the link step is a plain mutation keyed on nothing, called from `onSuccess` once the id
  // exists.
  const linkCandidate = useMutation<
    CampaignDetail,
    ApiFailureError,
    { campaignId: string; body: CampaignCandidateLink }
  >({
    mutationFn: ({ campaignId, body }) =>
      client.putParsed(`/campaigns/${campaignId}`, body, campaignDetailSchema),
  });

  function resetForm(): void {
    setName('');
    setNameTouched(false);
    setActiveFrom('');
    setActiveTo('');
    setActiveToTouched(false);
    setCandidate(null);
    setCreatedCampaignId(null);
    setStep('details');
    setAlliances([]);
    saveAlliances.reset();
    // A stale 409/422/link failure must not survive to the next open, or a reader who closed
    // after a refusal sees it again on an otherwise blank form.
    mutation.reset();
    linkCandidate.reset();
  }

  function handleOpenChange(next: boolean): void {
    if (!next && step === 'alliances' && createdCampaignId !== null) {
      openRecord(createdCampaignId);
      return;
    }
    if (!next) resetForm();
    props.onOpenChange(next);
  }

  const rawFailure = linkCandidate.isError
    ? linkCandidate.error.failure
    : mutation.isError
      ? mutation.error.failure
      : null;
  const noFreeSlot = rawFailure?.kind === 'conflict' ? rawFailure.noFreeSlot : undefined;
  const noFreeSlotCapacity: Capacity | null =
    noFreeSlot !== undefined
      ? {
          campaign_slots: noFreeSlot.campaignSlots,
          campaigns_in_use: noFreeSlot.campaignsInUse,
          slots_available:
            noFreeSlot.campaignSlots === null
              ? null
              : noFreeSlot.campaignSlots - noFreeSlot.campaignsInUse,
        }
      : null;
  // The slot-gate block above renders the conflict instead; a plain conflict hides the server's
  // own reason behind a stable message; a 422 naming `timezone` has no field left on this form to focus, so it renders
  // as the general banner along with everything else.
  const formDialogFailure: ApiFailure | null =
    rawFailure === null
      ? null
      : noFreeSlot !== undefined
        ? null
        : rawFailure.kind === 'conflict'
          ? { kind: 'conflict', message: nm('refused.reason'), referrers: [] }
          : rawFailure.kind === 'invalid' && rawFailure.field === 'timezone'
            ? { ...rawFailure, field: null }
            : rawFailure;
  const nameError = fieldErrorOf(rawFailure, 'name');
  const nameRef = useRef<HTMLInputElement>(null);

  const nameId = useId();
  const nameErrorId = useId();
  const activeFromId = useId();
  const activeToId = useId();

  // The dialog's owner outlives the navigation, so it is closed here rather than by unmounting.
  function openRecord(campaignId: string): void {
    resetForm();
    props.onOpenChange(false);
    void navigate({ to: RECORD_PATH, params: { campaignId } });
  }

  /** The link step, or straight to the record when there is no candidature to attach --
   *  shared between a fresh creation and a resubmit that resumes on `createdCampaignId`. */
  function linkThenNavigate(campaignId: string): void {
    if (candidate === null) {
      setStep('alliances');
      return;
    }
    linkCandidate.mutate(
      {
        campaignId,
        body: {
          name: name.trim(),
          // The campaign was created a moment ago with none, and `PUT` requires the key.
          description: null,
          timezone: DEFAULT_TIMEZONE,
          active_from: activeFrom.length > 0 ? activeFrom : null,
          active_to: activeTo.length > 0 ? activeTo : null,
          candidate,
        },
      },
      {
        onSuccess: () => {
          setStep('alliances');
        },
      },
    );
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const trimmed = name.trim();
    if (trimmed.length === 0) return;

    if (createdCampaignId !== null) {
      linkThenNavigate(createdCampaignId);
      return;
    }

    mutation.mutate(
      {
        name: trimmed,
        timezone: DEFAULT_TIMEZONE,
        active_from: activeFrom.length > 0 ? activeFrom : undefined,
        active_to: activeTo.length > 0 ? activeTo : undefined,
      },
      {
        onSuccess: (created) => {
          setCreatedCampaignId(created.id);
          linkThenNavigate(created.id);
        },
        onError: (error) => {
          // The one field this form can still 422 on -- `timezone` has no
          // input left to focus.
          const field =
            error instanceof ApiFailureError && error.failure.kind === 'invalid'
              ? error.failure.field
              : null;
          if (field === 'name') nameRef.current?.focus();
        },
      },
    );
  }

  if (step === 'alliances' && createdCampaignId !== null) {
    return (
      <FormDialog
        open={props.open}
        onOpenChange={handleOpenChange}
        title={nm('alliances.title')}
        description={nm('alliances.description')}
        submitLabel={nm('alliances.submit')}
        cancelLabel={nm('alliances.skip')}
        pending={saveAlliances.isPending}
        failure={saveAlliances.error}
        size="lg"
        onSubmit={(event) => {
          event.preventDefault();
          saveAlliances.mutate(alliances, { onSuccess: () => openRecord(createdCampaignId) });
        }}
      >
        <Steps current={3} />
        <AlliancesEditor
          campaignId={createdCampaignId}
          value={alliances}
          onChange={setAlliances}
          editable
        />
      </FormDialog>
    );
  }

  return (
    <FormDialog
      open={props.open}
      onOpenChange={handleOpenChange}
      title={nm('title')}
      submitLabel={nm('submit')}
      pending={mutation.isPending || linkCandidate.isPending}
      failure={formDialogFailure}
      onSubmit={handleSubmit}
    >
      <Steps current={1} />
      {noFreeSlotCapacity !== null && (
        <div className="flex flex-col gap-4">
          <Alert variant="destructive">
            <CircleAlert aria-hidden className="size-4 shrink-0" />
            <AlertDescription>{nm('refused.noSlots')}</AlertDescription>
          </Alert>
          <CapacityStats capacity={noFreeSlotCapacity} />
          <div>
            <RequestSlots
              tenantId={props.tenantId}
              capacity={noFreeSlotCapacity}
              trigger={
                <Button type="button" variant="outline">
                  {cm('slots.request')}
                </Button>
              }
            />
          </div>
        </div>
      )}

      <TseCandidatePicker
        tenantId={props.tenantId}
        value={candidate}
        onChange={setCandidate}
        onSuggestName={(suggested) => {
          if (!nameTouched) setName(suggested);
        }}
        onElectionDateChange={(date) => {
          // Election day is Fim, the collection window's own end -- never Início too, which
          // would otherwise autofill a one-day window (frame `96:2` draws months between them).
          if (date === null) return;
          if (!activeToTouched) setActiveTo(date);
        }}
      />

      <Field>
        <FieldLabel htmlFor={nameId}>{nm('name.label')}</FieldLabel>
        <Input
          id={nameId}
          ref={nameRef}
          required
          value={name}
          onChange={(event) => {
            setNameTouched(true);
            setName(event.target.value);
          }}
          {...fieldControlProps(rawFailure, 'name', nameErrorId)}
        />
        <FieldError id={nameErrorId}>{nameError}</FieldError>
      </Field>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field>
          <FieldLabel htmlFor={activeFromId}>{nm('activeFrom.label')}</FieldLabel>
          <Input
            id={activeFromId}
            type="date"
            value={activeFrom}
            onChange={(event) => setActiveFrom(event.target.value)}
          />
        </Field>

        <Field>
          <FieldLabel htmlFor={activeToId}>{nm('activeTo.label')}</FieldLabel>
          <Input
            id={activeToId}
            type="date"
            value={activeTo}
            onChange={(event) => {
              setActiveToTouched(true);
              setActiveTo(event.target.value);
            }}
          />
        </Field>
      </div>

      <LocalityStrip tenantId={props.tenantId} candidate={candidate} />
    </FormDialog>
  );
}
